import type { SurfaceProfileTarget } from "../../modeling/surfaceProfiles.ts";

type Vec3 = readonly [number, number, number];

interface Vec2 {
  u: number;
  v: number;
}

const EPSILON = 1e-9;

function sub(a: Vec3, b: Vec3): [number, number, number] {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function scale(v: Vec3, s: number): [number, number, number] {
  return [v[0] * s, v[1] * s, v[2] * s];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross(a: Vec3, b: Vec3): [number, number, number] {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function lengthSq(v: Vec3): number {
  return dot(v, v);
}

function normalize(v: Vec3): [number, number, number] | null {
  const lenSq = lengthSq(v);
  if (lenSq < EPSILON * EPSILON) return null;
  return scale(v, 1 / Math.sqrt(lenSq));
}

function distancePointSegmentSq2D(point: Vec2, a: Vec2, b: Vec2): number {
  const abU = b.u - a.u;
  const abV = b.v - a.v;
  const lenSq = abU * abU + abV * abV;
  if (lenSq < EPSILON * EPSILON) {
    const du = point.u - a.u;
    const dv = point.v - a.v;
    return du * du + dv * dv;
  }
  const t = Math.max(
    0,
    Math.min(1, ((point.u - a.u) * abU + (point.v - a.v) * abV) / lenSq),
  );
  const u = a.u + abU * t;
  const v = a.v + abV * t;
  const du = point.u - u;
  const dv = point.v - v;
  return du * du + dv * dv;
}

function distancePointSegmentSq3D(point: Vec3, a: Vec3, b: Vec3): number {
  const ab = sub(b, a);
  const lenSq = lengthSq(ab);
  if (lenSq < EPSILON * EPSILON) return lengthSq(sub(point, a));
  const t = Math.max(0, Math.min(1, dot(sub(point, a), ab) / lenSq));
  return lengthSq(
    sub(point, [a[0] + ab[0] * t, a[1] + ab[1] * t, a[2] + ab[2] * t]),
  );
}

function loopNormal(
  vertices: ReadonlyArray<Vec3>,
): [number, number, number] | null {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < vertices.length; i += 1) {
    const a = vertices[i]!;
    const b = vertices[(i + 1) % vertices.length]!;
    nx += (a[1] - b[1]) * (a[2] + b[2]);
    ny += (a[2] - b[2]) * (a[0] + b[0]);
    nz += (a[0] - b[0]) * (a[1] + b[1]);
  }
  const newell = normalize([nx, ny, nz]);
  if (newell) return newell;

  const origin = vertices[0];
  if (!origin) return null;
  for (let i = 1; i < vertices.length - 1; i += 1) {
    const normal = normalize(
      cross(sub(vertices[i]!, origin), sub(vertices[i + 1]!, origin)),
    );
    if (normal) return normal;
  }
  return null;
}

function loopBasis(
  vertices: ReadonlyArray<Vec3>,
): { origin: Vec3; tangent: Vec3; bitangent: Vec3 } | null {
  const origin = vertices[0];
  if (!origin || vertices.length < 3) return null;
  const normal = loopNormal(vertices);
  if (!normal) return null;

  for (let i = 0; i < vertices.length; i += 1) {
    const edge = sub(vertices[(i + 1) % vertices.length]!, vertices[i]!);
    const tangentRaw = sub(edge, scale(normal, dot(edge, normal)));
    const tangent = normalize(tangentRaw);
    if (!tangent) continue;
    const bitangent = normalize(cross(normal, tangent));
    if (bitangent) return { origin, tangent, bitangent };
  }
  return null;
}

function projectPoint(
  point: Vec3,
  basis: { origin: Vec3; tangent: Vec3; bitangent: Vec3 },
): Vec2 {
  const delta = sub(point, basis.origin);
  return {
    u: dot(delta, basis.tangent),
    v: dot(delta, basis.bitangent),
  };
}

function pointInPolygon(point: Vec2, polygon: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (
      a.v > point.v !== b.v > point.v &&
      point.u < ((b.u - a.u) * (point.v - a.v)) / (b.v - a.v) + a.u
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function nearestLoopEdge(
  point: Vec2,
  polygon: readonly Vec2[],
): { edgeIndex: number; distanceSq: number } | null {
  if (polygon.length < 2) return null;
  let bestEdge = 0;
  let bestDistanceSq = Number.POSITIVE_INFINITY;
  for (let i = 0; i < polygon.length; i += 1) {
    const distanceSq = distancePointSegmentSq2D(
      point,
      polygon[i]!,
      polygon[(i + 1) % polygon.length]!,
    );
    if (distanceSq < bestDistanceSq) {
      bestDistanceSq = distanceSq;
      bestEdge = i;
    }
  }
  return { edgeIndex: bestEdge, distanceSq: bestDistanceSq };
}

export function resolveSurfaceLoopProfileTarget(
  vertices: ReadonlyArray<Vec3>,
  point: Vec3,
  edgeRadius: number,
): SurfaceProfileTarget | null {
  const radius = Math.max(0, edgeRadius);
  const edgeRadiusSq = radius * radius;

  if (vertices.length === 2) {
    return distancePointSegmentSq3D(point, vertices[0]!, vertices[1]!) <=
      edgeRadiusSq
      ? { kind: "edge", edgeIndex: 0 }
      : null;
  }

  const basis = loopBasis(vertices);
  if (!basis) return null;
  const projectedPoint = projectPoint(point, basis);
  const projectedLoop = vertices.map((vertex) => projectPoint(vertex, basis));
  const nearest = nearestLoopEdge(projectedPoint, projectedLoop);
  if (!nearest) return null;
  if (nearest.distanceSq <= edgeRadiusSq) {
    return { kind: "edge", edgeIndex: nearest.edgeIndex };
  }
  return pointInPolygon(projectedPoint, projectedLoop)
    ? { kind: "face" }
    : null;
}
